/**
 * Missives inbox page.
 * Shows inbox, sent, and archived tabs with missive list.
 * Displays unread count badge and resource indicators.
 *
 * Features a fantasy parchment aesthetic with scroll-like borders
 * and warm paper-like colors for an immersive experience.
 */

import { Head, Link, router } from '@inertiajs/react';
import { FlashMessage } from '@/components/ui/FlashMessage';

/** Missive data from the backend */
interface MissiveData {
    id: number;
    subject: string;
    sender: { id: number; username: string } | null;
    recipient: { id: number; username: string } | null;
    status: { value: string; label: string };
    has_resources: boolean;
    resources_collected: boolean;
    is_read: boolean;
    is_recipient: boolean;
    sent_at: string | null;
    read_at: string | null;
}

/** Pagination link from Laravel */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/** Paginated response from Laravel */
interface PaginatedMissives {
    data: MissiveData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    missives: PaginatedMissives;
    tab: 'inbox' | 'sent' | 'archived';
    unreadCount: number;
}

/** Format date in a readable format - uses a more fantasy-appropriate style */
function formatDate(isoString: string | null): string {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Decorative corner flourish SVG component for parchment borders.
 * Rotated via className to create all four corners.
 */
function CornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-12 h-12 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Wax seal component for visual flair on unread missives.
 */
function WaxSeal({ collected = false }: { collected?: boolean }) {
    return (
        <div
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                collected
                    ? 'bg-gradient-to-br from-stone-400 to-stone-500'
                    : 'bg-gradient-to-br from-red-700 to-red-900'
            }`}
            style={{
                boxShadow: collected
                    ? 'inset 0 2px 4px rgba(0,0,0,0.3)'
                    : 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="text-xs font-serif text-amber-100/80">✦</span>
        </div>
    );
}

/**
 * Missives Index page - displays inbox, sent, and archived missives.
 * Features a fantasy parchment scroll aesthetic.
 */
export default function Index({ missives, tab, unreadCount }: Props) {
    /** Handle tab change */
    const handleTabChange = (newTab: string) => {
        router.get('/missives', { tab: newTab }, { preserveState: true });
    };

    return (
        <>
            <Head title="Missives" />
            <FlashMessage />

            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header - kept in dark theme */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <Link
                                href="/map"
                                className="text-slate-300 hover:text-white transition-colors"
                            >
                                ← Back to Map
                            </Link>
                            <h1 className="text-3xl font-bold flex items-center gap-2 font-serif">
                                <span className="text-amber-400">📜</span> Missives
                                {unreadCount > 0 && (
                                    <span className="px-2.5 py-1 bg-gradient-to-br from-red-700 to-red-900 text-sm rounded-full shadow-lg border border-red-600/50">
                                        {unreadCount}
                                    </span>
                                )}
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link
                                href="/missives/blocks"
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
                            >
                                🚫 Blocked
                            </Link>
                            <Link
                                href="/missives/create"
                                className="px-4 py-2 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 rounded-lg font-medium transition-all shadow-lg border border-amber-500/30"
                            >
                                ✒️ Compose
                            </Link>
                        </div>
                    </div>

                    {/* Parchment scroll container */}
                    <div className="relative">
                        {/* Parchment background with aged paper texture */}
                        <div
                            className="relative rounded-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                            }}
                        >
                            {/* Decorative corner flourishes */}
                            <CornerFlourish className="absolute top-2 left-2" />
                            <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                            <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                            <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                            {/* Aged paper edge effect - top */}
                            <div
                                className="absolute top-0 left-0 right-0 h-3"
                                style={{
                                    background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)',
                                }}
                            />

                            {/* Aged paper edge effect - bottom */}
                            <div
                                className="absolute bottom-0 left-0 right-0 h-3"
                                style={{
                                    background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)',
                                }}
                            />

                            {/* Inner content area */}
                            <div className="relative p-6 pt-8">
                                {/* Ornate header with tab title */}
                                <div className="text-center mb-6">
                                    <div className="inline-block">
                                        <div className="flex items-center gap-3">
                                            <span className="text-amber-800/40">━━━━━━</span>
                                            <h2 className="text-2xl font-serif text-amber-900 tracking-wide">
                                                {tab === 'inbox' && '📥 Received Correspondence'}
                                                {tab === 'sent' && '📤 Dispatched Letters'}
                                                {tab === 'archived' && '📦 Archived Documents'}
                                            </h2>
                                            <span className="text-amber-800/40">━━━━━━</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs - styled as wax seal tabs */}
                                <div className="flex justify-center gap-2 mb-6">
                                    <TabButton
                                        label="Inbox"
                                        icon="📥"
                                        isActive={tab === 'inbox'}
                                        badge={unreadCount > 0 ? unreadCount : undefined}
                                        onClick={() => handleTabChange('inbox')}
                                    />
                                    <TabButton
                                        label="Sent"
                                        icon="📤"
                                        isActive={tab === 'sent'}
                                        onClick={() => handleTabChange('sent')}
                                    />
                                    <TabButton
                                        label="Archived"
                                        icon="📦"
                                        isActive={tab === 'archived'}
                                        onClick={() => handleTabChange('archived')}
                                    />
                                </div>

                                {/* Decorative divider */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                    <span className="text-amber-800/40 text-lg">✦</span>
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                </div>

                                {/* Missive List */}
                                {missives.data.length === 0 ? (
                                    <EmptyState tab={tab} />
                                ) : (
                                    <div className="space-y-3">
                                        {missives.data.map((missive) => (
                                            <MissiveRow key={missive.id} missive={missive} tab={tab} />
                                        ))}
                                    </div>
                                )}

                                {/* Pagination */}
                                {missives.last_page > 1 && (
                                    <Pagination links={missives.links} />
                                )}

                                {/* Bottom flourish */}
                                <div className="flex items-center justify-center gap-2 mt-8">
                                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                                    <span className="text-amber-800/30 text-sm">❧</span>
                                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                                </div>
                            </div>
                        </div>

                        {/* Shadow beneath the parchment */}
                        <div
                            className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                            style={{
                                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)',
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Tab button component - styled as parchment tabs with wax seal aesthetic.
 */
function TabButton({
    label,
    icon,
    isActive,
    badge,
    onClick,
}: {
    label: string;
    icon: string;
    isActive: boolean;
    badge?: number;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 rounded-lg font-serif font-medium transition-all flex items-center gap-2 ${
                isActive
                    ? 'bg-gradient-to-br from-amber-700 to-amber-900 text-amber-100 shadow-lg border border-amber-600/50'
                    : 'bg-amber-900/20 text-amber-800 hover:bg-amber-900/30 border border-amber-800/20'
            }`}
            style={isActive ? {
                boxShadow: '0 2px 8px rgba(139,90,43,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
            } : undefined}
        >
            <span>{icon}</span>
            {label}
            {badge !== undefined && badge > 0 && (
                <span
                    className="px-1.5 py-0.5 text-xs rounded-full bg-gradient-to-br from-red-600 to-red-800 text-white"
                    style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)' }}
                >
                    {badge}
                </span>
            )}
        </button>
    );
}

/**
 * Empty state component - styled with parchment theme.
 */
function EmptyState({ tab }: { tab: string }) {
    const messages: Record<string, { icon: string; text: string; subtext: string }> = {
        inbox: {
            icon: '📭',
            text: 'Your correspondence box awaits...',
            subtext: 'No letters have arrived yet.',
        },
        sent: {
            icon: '✒️',
            text: 'Your quill rests idle...',
            subtext: 'You have not dispatched any missives.',
        },
        archived: {
            icon: '📜',
            text: 'The archives are empty...',
            subtext: 'No documents have been stored away.',
        },
    };

    const { icon, text, subtext } = messages[tab] || messages.inbox;

    return (
        <div className="text-center py-12">
            <span className="text-5xl mb-4 block opacity-60">{icon}</span>
            <p className="text-xl font-serif text-amber-900/80 italic mb-2">{text}</p>
            <p className="text-amber-800/60 text-sm">{subtext}</p>
            {tab === 'inbox' && (
                <Link
                    href="/missives/create"
                    className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-gradient-to-br from-amber-700 to-amber-800 hover:from-amber-600 hover:to-amber-700 rounded-lg text-amber-100 font-serif transition-all shadow-lg border border-amber-600/30"
                >
                    ✒️ Compose your first letter
                </Link>
            )}
        </div>
    );
}

/**
 * Missive row component - styled as a folded letter/envelope.
 */
function MissiveRow({ missive, tab }: { missive: MissiveData; tab: string }) {
    const isUnread = !missive.is_read && tab === 'inbox';
    const otherUser = tab === 'sent' ? missive.recipient : missive.sender;

    return (
        <Link
            href={`/missives/${missive.id}`}
            className={`block relative rounded-lg transition-all group ${
                isUnread
                    ? 'hover:translate-x-1'
                    : 'hover:translate-x-1 opacity-80 hover:opacity-100'
            }`}
        >
            {/* Letter/envelope background */}
            <div
                className={`p-4 rounded-lg border-2 transition-all ${
                    isUnread
                        ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-400/60 shadow-md'
                        : 'bg-gradient-to-br from-stone-100 to-stone-200 border-stone-300/60'
                }`}
                style={{
                    boxShadow: isUnread
                        ? '0 2px 8px rgba(139,90,43,0.2), inset 0 0 20px rgba(255,255,255,0.5)'
                        : '0 1px 3px rgba(0,0,0,0.1)',
                }}
            >
                <div className="flex items-start justify-between gap-4">
                    {/* Left side: wax seal indicator for unread */}
                    <div className="flex items-start gap-3">
                        {isUnread ? (
                            <WaxSeal />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-stone-300/50 flex items-center justify-center">
                                <span className="text-stone-500 text-xs">✓</span>
                            </div>
                        )}

                        <div className="flex-1 min-w-0">
                            {/* Subject */}
                            <h3 className={`font-serif truncate ${
                                isUnread
                                    ? 'text-amber-900 font-semibold'
                                    : 'text-stone-700'
                            }`}>
                                {missive.subject}
                            </h3>

                            {/* From/To line */}
                            <p className="text-sm mt-1 font-serif italic">
                                <span className="text-amber-800/60">
                                    {tab === 'sent' ? 'To: ' : 'From: '}
                                </span>
                                <span className={isUnread ? 'text-amber-900' : 'text-stone-600'}>
                                    {otherUser?.username || 'Unknown'}
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Right side: date and resource indicator */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Resource indicator - styled as a small pouch/bundle */}
                        {missive.has_resources && (
                            <div
                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-serif ${
                                    missive.resources_collected
                                        ? 'bg-stone-300/50 text-stone-500'
                                        : 'bg-amber-200 text-amber-800 border border-amber-400/50'
                                }`}
                                title={missive.resources_collected ? 'Resources collected' : 'Contains resources'}
                            >
                                <span>💰</span>
                                {!missive.resources_collected && <span>Gift</span>}
                            </div>
                        )}

                        {/* Date - styled as handwritten */}
                        <span className="text-xs text-amber-800/50 font-serif italic">
                            {formatDate(missive.sent_at)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Subtle fold line decoration */}
            <div
                className="absolute top-0 right-8 w-px h-full opacity-20"
                style={{
                    background: 'linear-gradient(to bottom, transparent, rgba(139,90,43,0.3), transparent)',
                }}
            />
        </Link>
    );
}

/**
 * Pagination component - styled with parchment theme.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-8 flex justify-center items-center gap-1">
            <span className="text-amber-800/40 mr-2">❮</span>
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1.5 rounded font-serif text-sm transition-all ${
                        link.active
                            ? 'bg-gradient-to-br from-amber-700 to-amber-800 text-amber-100 shadow-md'
                            : link.url
                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300/50'
                            : 'bg-stone-200/50 text-stone-400 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
            <span className="text-amber-800/40 ml-2">❯</span>
        </div>
    );
}

